import { IEKGEvent } from '../store/ekgImport.types'
import { IItem } from '../../timeline/store/timeline.types'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { IOperationsState } from '../../procedures/operations/store/operations.types'
import { getOperationsAction } from '../../procedures/operations/utilities/saveOperations.util'
import { generateClientCorrelationId } from '../../nemsisData/utils/customElements.util'
import { INemsisSnomedLookup } from '../../../components/procedure/store/procedure.types'
import { getUpdatedProcedureStateWithNemsisCode, handleNemsisCodeError } from '../../../utilities/nemsisCode.util'

interface CreateOperationsProcedureProps {
  procedureEvent: IEKGEvent
  ptaCutoff: Date | null
  nemsisCodeOptions: INemsisSnomedLookup[]
}

export interface OperationsProcedureObject {
  operationsState: IOperationsState
  timelineItem: IItem
}

const codeForOperations = {
  stretcherChairMovingTo: 'Stretcher',
  jointCommissionTimeOut: 'TimeOut',
  safetyBrief: 'SafetyBrief',
  hearingPrecautions: 'HearingPrecautions',
  environmentalControls: 'Environmental',
  safetyRestraints: 'SafetyRestraints',
  seizurePrecautions: 'SeizurePrecautions',
  patientLoadedCold: 'LoadedCold',
  patientLoadedHot: 'LoadHot',
  patientOffloadCold: 'OffloadCold',
  patientOffloadHot: 'OffloadHot',
  patientMonitoring: 'PatientMonitoring',
  pediatricStretcherChair: 'PediStretcher',
  pediatricSafetyRestraintDevice: 'Pediatric Safety Restraint Device',
  babyPod: 'BabyPod',
  pediMate: 'PediMate',
  patientPersonalProtectiveEquipment: 'PatientPPE',
}
type ToggleCodes = keyof typeof codeForOperations

const createOperationsProcedure = (props: CreateOperationsProcedureProps): OperationsProcedureObject => {
  const { procedureEvent, ptaCutoff, nemsisCodeOptions } = props

  const clientEventId = (procedureEvent.eventVitals.clientEventId as number).toString()
  const vsAction = procedureEvent.eventVitals.vsAction
  const isEkgImport = procedureEvent.eventVitals.isEkgImport

  const itemComments = procedureEvent.procedureComments || ''
  const itemAction = procedureEvent.procedureAction || ''
  const itemSubHeader = `By , Attempts: 1, Success: Yes, Comments: ${itemComments}.`

  const eventDate = new Date(procedureEvent.dtDevice)
  const isPTA = !!ptaCutoff && eventDate < ptaCutoff

  const toggleOptions: Record<string, boolean> = {}
  Object.keys(codeForOperations).forEach((toggleCode) => {
    const toggleValue = codeForOperations[toggleCode as ToggleCodes]
    toggleOptions[toggleCode] = toggleValue === itemAction
  })

  let operationsState: IOperationsState = {
    key: clientEventId,
    rightPatient: false,
    rightProcedure: false,
    rightConsent: false,
    rightSiteSide: false,
    rightPosition: false,
    complete: false,
    lastName: '',
    firstName: '',
    weight: null,
    weightUnit: null, // kilograms
    ...(toggleOptions as Record<ToggleCodes, boolean>),
    assessedBy: null,
    procedureComplications: [],
    response: null,
    responsePNNV: null,
    authorization: null,
    comments: itemComments,
    addToTimeline: true,
    updateInputRows: false,
    date: procedureEvent.eventVitals.date,
    time: procedureEvent.eventVitals.time,
    successfulAttempt: true,
    pta: isPTA,
    attempts: '1',
    type: 'Operations',
    rolePerforming: null,
    rolePerformingPNNV: null,
    jointCommissionTimeOutArray: [],
    isEkgImport: 1,
    nemsisCode: null,
    procedurePerformedByAnotherUnit: null,
    procedurePerformedByAnotherUnitPNNV: null,
  }

  const clientCorrelationId = generateClientCorrelationId(
    nemsisCodeOptions,
    TimelineConstants.OPERATIONS_PROCEDURE,
    getOperationsAction(operationsState),
    {
      filter1: null,
      filter2: null,
      filter3: null,
    },
    clientEventId
  )

  const timelineItem: IItem = {
    key: clientEventId,
    clientCorrelationId,
    itemType: 'Procedures',
    event: 'Operations',
    grid: true,
    labelHeaderFlag: isPTA ? 'PTA' : '',
    labelHeader: procedureEvent.eventVitals.time,
    subHeader: procedureEvent.eventVitals.date,
    eventSubHeader: itemSubHeader,
    rowActionType: TimelineConstants.SET_OPERATIONS_PROCEDURE_ITEM,
    defaultHref: TabConstants.OPERATIONS_PROCEDURE,
    date: new Date(procedureEvent.dtDevice),
    clientEventId: clientEventId,
    ...(vsAction && { vsAction: vsAction }),
    ...(isEkgImport && { isEkgImport: isEkgImport }),
  }

  try {
    operationsState = getUpdatedProcedureStateWithNemsisCode({
      procedureState: operationsState,
      actionField: getOperationsAction,
      filter1Field: null,
      filter2Field: null,
      filter3Field: null,
      nemsisCodeOptions,
    })
  } catch (error: unknown) {
    operationsState = handleNemsisCodeError(error, operationsState)
  }

  const newOerationsProcedure: OperationsProcedureObject = {
    operationsState,
    timelineItem,
  }

  return newOerationsProcedure
}

export default createOperationsProcedure
